import React, { useMemo } from "react";
import { UploadProgress as UploadProgressType } from "../../types/upload";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";

interface UploadProgressProps {
  file: File;
  progress: UploadProgressType;
  imageAllowed?: boolean;
}

export const UploadProgress: React.FC<UploadProgressProps> = ({
  file,
  progress,
  imageAllowed,
}) => {
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);

  return (
    <div className="bg-white p-2 sm:p-3 md:p-4 rounded-lg shadow w-full">
      <div className="flex flex-col xs:flex-row items-center gap-2 sm:gap-3 md:gap-4">
        <div className="w-full xs:w-24 sm:w-28 md:w-32 h-16 sm:h-18 md:h-20 bg-gray-100 rounded flex items-center justify-center">
          {imageAllowed ? (
            <img
              src={objectUrl}
              alt={file.name}
              className="max-h-full max-w-full object-contain rounded-md"
            />
          ) : (
            <video
              src={objectUrl}
              className="max-h-full max-w-full object-contain rounded-md"
            ></video>
          )}
        </div>
        <div className="flex-1 w-full">
          <p className="font-medium text-gray-900 line-clamp-2 text-sm sm:text-base text-wrap mr-8">
            {file.name}
          </p>
          <p className="text-xs sm:text-sm text-gray-500">
            {Math.round((file.size / 1024 / 1024) * 100) / 100} MB
          </p>

          <div className="mt-1.5 sm:mt-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2 sm:h-2.5">
                <div
                  className="bg-customGreenColor h-2 sm:h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                ></div>
              </div>
              <span className="text-xs sm:text-sm text-gray-600 min-w-[35px] sm:min-w-[40px]">
                {progress.progress}%
              </span>
            </div>

            <div className="mt-1 flex items-center gap-1">
              {progress.status === "completed" && (
                <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
              )}
              {progress.status === "error" && (
                <XCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
              )}
              <span className="text-xs sm:text-sm text-gray-600 capitalize">
                {progress.status}
              </span>
              {progress.error && (
                <span className="text-xs sm:text-sm text-red-500 ml-2">
                  {progress.error}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
